// Transport-independent durable queue. Storage must commit atomically before a
// local success is returned. Native uses encrypted, private Keystore storage.
export const cachedPaths = ['/bootstrap', '/savings/goals', '/notepad'];
export function target(path) {
  let m = path.match(/^\/data\/(expenses|incomes|savings|loans|recurring)(?:\/([\w-]+))?$/);
  if (m) return { cache: '/bootstrap', kind: m[1], id: m[2] };
  m = path.match(/^\/(savings\/goals|notepad)(?:\/([\w-]+))?$/);
  if (m) return { cache: m[1] === 'notepad' ? '/notepad' : '/savings/goals', kind: m[1] === 'notepad' ? null : 'goals', id: m[2] };
  if (path === '/savings/transfer') return { cache: '/bootstrap', kind: 'savings', transfer: true };
  return path === '/settings' ? { cache: '/bootstrap', kind: 'settings' } : null;
}
const copy = (v) => JSON.parse(JSON.stringify(v));
const rows = (cache, t) => t.kind ? cache?.[t.kind] : cache;
const error = (message) => new Error(message);
const fresh = () => ({ version: 1, user: null, session: null, cache: {}, aliases: {}, queue: [], failure: '', nextRetry: 0, attempts: 0, needsRefresh: false });
function replaceIds(value, oldId, newId, field = '') {
  if (value === oldId && ['id', 'goal_id', 'from_goal', 'to_goal'].includes(field)) return newId;
  if (Array.isArray(value)) return value.map((v) => replaceIds(v, oldId, newId));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, replaceIds(v, oldId, newId, k)]));
  return value;
}
function apply(cache, op, result) {
  const t = target(op.path);
  if (!cache[t.cache]) return;
  if (t.kind === 'settings') { cache[t.cache].settings = result; return; }
  const list = rows(cache[t.cache], t);
  if (!list) return;
  const id = t.id || op.localId;
  const next = list.filter((r) => r.id !== id && r.id !== result?.id);
  if (op.method !== 'DELETE') next.push(result);
  if (t.kind) cache[t.cache][t.kind] = next; else cache[t.cache] = next;
}
export class OfflineStore {
  constructor({ storage, send, online = () => navigator.onLine, changed = () => {}, uuid = () => crypto.randomUUID(), now = () => Date.now() }) {
    Object.assign(this, { storage, send, online, changed, uuid, now });
    this.state = fresh(); this.serial = Promise.resolve(); this.generation = 0; this.running = null;
  }
  exclusive(fn) { const p = this.serial.then(fn); this.serial = p.catch(() => {}); return p; }
  async commit(next) { await this.storage.set(copy(next)); this.state = next; this.changed(); }
  async restore(token) {
    const state = await this.storage.get();
    if (state?.version === 1 && state.session === token && token) this.state = state;
    else this.state = fresh(); // Locked data is preserved for same-account reauthentication.
    this.changed(); return this.state.user;
  }
  async identify(user, token, backgroundAllowed = false) {
    return this.exclusive(async () => {
      if (this.state.user?.user_id !== user.user_id) {
        this.generation++;
        const stored = await this.storage.get();
        if (stored?.user?.user_id === user.user_id) this.state = stored;
        else { await this.storage.remove(); this.state = fresh(); }
      }
      await this.commit({ ...this.state, user: copy(user), session: token, backgroundAllowed });
    });
  }
  async lock() {
    this.generation++;
    return this.exclusive(async () => {
      const current = this.state.user ? this.state : await this.storage.get();
      if (current?.user) await this.storage.set(copy({ ...current, session: null, backgroundAllowed: false }));
      this.state = fresh(); this.changed();
    });
  }
  async clear() {
    this.generation++; this.state = fresh(); this.changed();
    return this.exclusive(async () => { await this.storage.remove(); this.state = fresh(); this.changed(); });
  }
  view() {
    const cache = copy(this.state.cache);
    for (const op of this.state.queue) apply(cache, op, op.preview);
    // Derive savings balances from the same local ledger as the dashboard.
    if (cache['/savings/goals'] && cache['/bootstrap']) {
      const balances = { general: 0 };
      const add = (id, cents) => { balances[id] = (balances[id] || 0) + cents; };
      for (const r of cache['/bootstrap'].savings || []) {
        add(r.goal_id || 'general', Math.round(r.amt * 100));
        if (r.transfer_amount) { add(r.from_goal, -Math.round(r.transfer_amount * 100)); add(r.to_goal, Math.round(r.transfer_amount * 100)); }
      }
      cache['/savings/goals'].balances = Object.fromEntries(Object.entries(balances).map(([k, v]) => [k, v / 100]));
    }
    return cache;
  }
  status() {
    return { label: !this.online() ? 'Offline' : this.running ? 'Syncing' : this.state.failure || this.state.needsRefresh ? 'Sync failed' : this.state.queue.length ? 'Pending sync' : 'Synced',
      pending: this.state.queue.length, busy: !!this.running, error: this.state.failure, blocked: !!this.state.queue[0]?.blocked };
  }
  async read(path, requireServer = false) {
    const generation = this.generation;
    if (this.online() && !this.state.queue.length) {
      let data, received = false;
      try { data = await this.send('GET', path); received = true; }
      catch (e) { if (e.response || requireServer) throw e; }
      if (received) {
        await this.exclusive(async () => {
          if (generation !== this.generation) throw error("Account changed; retry after signing in.");
          const next = copy(this.state); next.cache[path] = data; await this.commit(next);
        });
        return this.view()[path];
      }
    }
    if (requireServer) throw error('A verified server refresh needs an online connection.');
    const data = this.view()[path];
    if (data === undefined) throw error('Not available offline yet. Connect and tap Retry to load this data.');
    return data;
  }
  async enqueue(method, path, body) {
    return this.exclusive(async () => {
      if (this.state.needsRefresh) throw error('Reload the server copy before making another change. Tap Retry sync.');
      if (!this.state.user?.age_18_confirmed) throw error('Sign in online and confirm you are 18 or older first.');
      if (this.state.queue.length >= 500) throw error('Offline queue is full. Sync your changes first.');
      for (const [oldId, newId] of Object.entries(this.state.aliases || {})) {
        path = path.replace(`/${oldId}`, `/${newId}`); body = replaceIds(body, oldId, newId);
      }
      const t = target(path); const cache = this.view()[t.cache];
      // New notes/goals can be created before their list is first opened.
      if (cache === undefined && t.cache !== '/bootstrap') {
        const next = copy(this.state); next.cache[t.cache] = t.cache === '/notepad' ? [] : { goals: [], balances: {} }; await this.commit(next);
      }
      const currentCache = this.view()[t.cache];
      const base = t.kind === 'settings' ? currentCache?.settings : t.id ? rows(currentCache, t)?.find((r) => r.id === t.id) : null;
      if ((method !== 'POST' && !base) || !currentCache) throw error('Load this record online before editing offline.');
      const localId = `local_${this.uuid().replace(/-/g, '')}`;
      let preview = method === 'DELETE' ? { ok: true } : t.kind === 'settings' ? { ...base, ...body } : { ...base, ...body, id: t.id || localId };
      if (method === 'POST') preview = { created_at: this.now(), ...preview };
      if (t.cache === '/notepad' && method !== 'DELETE') preview = { content: '', color: '#fff7c2', pinned: false, drawing: [], ...preview, updated_at: this.now() };
      if (t.transfer) preview = { ...preview, amt: 0, id: body.request_id, transfer_amount: body.amount };
      const op = { operation_id: this.uuid(), method, path, body: copy(body), base: base || null, localId: t.transfer ? body.request_id : localId, preview };
      const next = copy(this.state); next.queue.push(op); await this.commit(next);
      return preview;
    });
  }
  async sync(force = false) {
    if (this.running) return this.running;
    if (!this.online() || !this.state.user || (!force && this.now() < this.state.nextRetry)) return;
    const generation = this.generation;
    this.running = this.drain(generation, force).finally(() => { this.running = null; this.changed(); });
    this.changed(); return this.running;
  }
  async drain(generation, force) {
    while (this.state.queue.length && this.online() && generation === this.generation) {
      const op = copy(this.state.queue[0]);
      if (op.blocked && !force) return;
      try {
        const response = await this.send('POST', '/offline/mutate', { operation_id: op.operation_id, method: op.method, path: op.path, body: op.body, base: op.base });
        if (response.operation_id !== op.operation_id) throw error('The server returned an unexpected sync acknowledgement. Your change is kept.');
        const { result } = response;
        if (generation !== this.generation) return;
        await this.exclusive(async () => {
          if (generation !== this.generation) return;
          if (this.state.queue[0]?.operation_id !== op.operation_id) throw error('The queue changed while syncing. Reload before retrying.');
          const next = copy(this.state); next.queue.shift(); apply(next.cache, op, result);
          const oldId = target(op.path).id || op.localId;
          if (result?.id) {
            next.aliases ||= {}; next.aliases[oldId] = result.id;
            next.queue = next.queue.map((item) => ({ ...replaceIds(item, oldId, result.id), path: item.path.replace(`/${oldId}`, `/${result.id}`) }));
            const following = next.queue.find((item) => target(item.path)?.id === result.id);
            if (following) following.base = result;
          } else if (op.path === '/settings') {
            const following = next.queue.find((item) => item.path === '/settings'); if (following) following.base = result;
          }
          next.failure = ''; next.attempts = 0; next.nextRetry = 0; await this.commit(next);
        });
      } catch (e) {
        if (generation !== this.generation) return;
        await this.exclusive(async () => {
          if (generation !== this.generation || !this.state.queue[0]) return;
          const next = copy(this.state); const code = e.response?.status;
          next.queue[0].blocked = !!code && code !== 423 && code !== 429 && code < 500;
          const detail = e.response?.data?.detail;
          next.failure = String(detail?.msg || detail || e.message || 'Sync failed. Retry when connected.');
          if (detail && typeof detail === 'object' && 'server' in detail) next.queue[0].server = detail.server;
          next.attempts++; next.nextRetry = this.now() + Math.min(300000, 1000 * 2 ** Math.min(next.attempts, 8));
          await this.commit(next);
        }); return;
      }
    }
    if (!this.state.queue.length && generation === this.generation) {
      let refreshed = true;
      for (const path of cachedPaths) {
        try { await this.read(path, true); } catch { refreshed = false; }
        if (generation !== this.generation) return;
      }
      await this.exclusive(async () => {
        if (generation !== this.generation) return;
        const next = copy(this.state);
        next.needsRefresh = !refreshed;
        next.failure = refreshed ? '' : 'The server copy could not be refreshed. Connect and tap Retry sync.';
        await this.commit(next);
      });
    }
  }
  async discardFirst() {
    return this.exclusive(async () => {
      if (this.running) throw error('Wait for the current sync attempt to finish before discarding changes.');
      if (!this.online()) throw error('Connect before discarding so the server copy can be verified.');
      // Only discard the entire remaining batch: later changes can depend on
      // provisional identities and earlier amounts. Requires explicit UI consent.
      const next = copy(this.state); next.queue = []; next.failure = ''; next.nextRetry = 0; next.needsRefresh = true;
      await this.commit(next);
    });
  }
}
